import { ParsedQuery } from "query-string";

import { Section, TableBody } from "components/layout";
import { TabbedTable } from "containers/TabbedTable";
import {
  RELEASE_STAGE_LABELS,
  RELEASE_STAGES,
  TiVoPublications,
  usePublications,
} from "hooks/tivoApps";
import { useSearchParams } from "hooks/utils";
import { ApiQueryParams } from "types/api";

import { DEVICE_APPS_LIMIT, TEXTS } from "../constants";
import { TiVoDeviceAppRow } from "./TiVoDeviceAppRow";
import styles from "./TiVoDeviceAppsList.scss";

interface TiVoDeviceAppsListProps {
  platformId: string;
  oemId: string;
  clientTypeId?: string;
}

const buildQueryParams = (
  params: ParsedQuery<string>,
  oemId: string,
  clientTypeId?: string
) => ({
  ...(clientTypeId ? { publishedToClientType: [clientTypeId] } : {}),
  publishedToOem: [oemId],
  page: Number(params.page) || 1,
  limit: DEVICE_APPS_LIMIT,
});

export function TiVoDeviceAppsList({
  platformId,
  oemId,
  clientTypeId,
}: TiVoDeviceAppsListProps) {
  const params = useSearchParams();
  const queryParams: ApiQueryParams = buildQueryParams(
    params,
    oemId,
    clientTypeId
  );

  const {
    isLoading,
    data: publicationsData,
    error,
  } = usePublications(platformId, {
    ...queryParams,
    include: ["variant", "variant.provider"],
  });

  const tabsConfig = [
    {
      columns: [
        {
          id: "provider",
          label: TEXTS.APPLICATION_NAME_COL,
          className: styles.providerCol,
        },
        {
          id: "variant",
          label: TEXTS.VARIANT_COL,
          className: styles.variantCol,
        },
        ...RELEASE_STAGES.map((rs) => ({
          id: rs,
          label: RELEASE_STAGE_LABELS[rs],
          className: styles.releaseStageCol,
        })),
      ],
      dataTestId: "device-applications-list",
    },
  ];

  const renderTableBody = (publications: TiVoPublications[]) => {
    return (
      <TableBody>
        {publications.map((publication: TiVoPublications) => {
          // This case should only throw error if backend for some reason has corrupted data
          if (!publication.variant || !publication.variant?.provider) {
            throw Error("Missing data");
          }

          return (
            <TiVoDeviceAppRow
              key={publication.id}
              provider={publication.variant.provider}
              variant={publication.variant}
              publication={publication}
              platformId={platformId}
              oemId={oemId}
              clientTypeId={clientTypeId}
            />
          );
        })}
      </TableBody>
    );
  };

  return (
    <Section header={TEXTS.APPLICATIONS_TAB}>
      <TabbedTable
        tabsConfig={tabsConfig}
        data={{
          loading: isLoading,
          results: publicationsData?.results || [],
          count: publicationsData?.meta?.total,
          error: error,
        }}
        renderTableBody={renderTableBody}
        layout={"fixed"}
      />
    </Section>
  );
}
